<template>
<div>

    <p>Select the earning types that count toward this plan&rsquo;s contribution basis. Both employee contributions and the employer match are calculated on the total of the selected earnings.</p>

    <p class="text-muted">
        <i class="far fa-info-circle" aria-hidden="true"></i>
        Earning types are configured per company. A company left with nothing selected falls back to all real earnings including tips, excluding imputed income.
    </p>

    <!-- Drill-down: one company's earning types -->
    <div v-if="selectedCompany">
        <p>
            <a href @click.prevent="selectedCompanyId = null">
                <i class="far fa-angle-left" aria-hidden="true"></i> All companies
            </a>
        </p>

        <div class="mb-2">
            <div class="p-1 unpad-x bg-150 rounded">
                <div><span class="text-muted">Company: </span>{{ selectedCompany.name }}</div>
            </div>
        </div>

        <!-- NB: keyed on the company so switching companies remounts the selector.
             FastTable keeps its own selectedRows state, and reusing one instance across
             companies would carry the previous company's selection into the new one. -->
        <company-earning-type-selector
            :key="selectedCompanyId"
            :company-earning-types="earningTypesForCompany(selectedCompanyId)"
            :model-value="formData.company_earning_types[selectedCompanyId] || []"
            @update:model-value="setCompanySelection(selectedCompanyId, $event)"
        />
    </div>

    <!-- Company index -->
    <div v-else>
        <table class="table pt-4">
            <thead>
                <tr>
                    <th>Company</th>
                    <th>Contribution Basis</th>
                    <th></th>
                </tr>
            </thead>

            <tbody>
                <tr v-for="company in enabledCompanies" :key="company.id" class="cursor-pointer" @click="selectedCompanyId = company.id">
                    <td>{{ company.name }}</td>
                    <td>
                        <span v-if="countForCompany(company.id)">{{ countForCompany(company.id) }} of {{ earningTypesForCompany(company.id).length }} earning types</span>
                        <span v-else class="text-muted">All real earnings including tips</span>
                    </td>
                    <td class="text-right">
                        <a href @click.prevent="selectedCompanyId = company.id">Edit</a>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</div>
</template>

<script>
import FormMixin from '@/mixins/Form'
import CompanyEarningTypeSelector from '@/pages/client/Settings/components/CompanyEarningTypeSelector'

export default {
    // `planNumbers` is the map built by PlanNumbersForm, so its keys are exactly the
    // companies this plan is being created for.
    props: ['initialValue', 'client', 'planNumbers', ],
    mixins: [FormMixin],
    components: {CompanyEarningTypeSelector, },
    computed: {
        enabledCompanies() {
            const companyIds = Object.keys(this.planNumbers || {}).map(id => '' + id)
            return this.client.companies.filter(c => companyIds.includes('' + c.id))
        },
        selectedCompany() {
            if (!this.selectedCompanyId) {return null}
            return this.client.companies.find(c => c.id == this.selectedCompanyId)
        },
    },
    data() {
        return {
            formData: this.makeFormData(),
            selectedCompanyId: null,
            allEarningTypes: [],
            earningTypesLoaded: false,
            errors: {},
        }
    },
    mounted() {
        this.getEarningTypes()
    },
    methods: {
        getEarningTypes() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/earning-types`).then(resp => {
                this.allEarningTypes = resp
                this.earningTypesLoaded = true
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        makeFormData() {
            const data = {
                company_earning_types: {},
            }

            Object.keys(this.initialValue || {}).forEach(companyId => {
                data.company_earning_types[companyId] = Array.from(this.initialValue[companyId])
            })

            return data
        },
        earningTypesForCompany(companyId) {
            return this.allEarningTypes.filter(cet => cet.company_id == companyId)
        },
        countForCompany(companyId) {
            return (this.formData.company_earning_types[companyId] || []).length
        },
        setCompanySelection(companyId, companyEarningTypeIds) {
            this.formData.company_earning_types[companyId] = companyEarningTypeIds
        },
        goBack() {
            // Returns whether this component consumed the Back action. The wizard's
            // Back button should return to the company list when the user is drilled
            // into one company, and only leave the step from the list itself.
            if (this.selectedCompanyId) {
                this.selectedCompanyId = null
                return true
            }

            return false
        },
        validate() {
            // Nothing is required here: a company with no configured earning types
            // deliberately falls back to all real earnings including tips.
            return true
        },
        getFormData() {
            // Drop companies that are no longer enabled, in case the user went back and
            // changed the plan numbers step after configuring a basis.
            const enabledIds = this.enabledCompanies.map(c => '' + c.id)
            const result = {}

            Object.keys(this.formData.company_earning_types).forEach(companyId => {
                if (enabledIds.includes('' + companyId) && this.formData.company_earning_types[companyId].length) {
                    result[companyId] = this.formData.company_earning_types[companyId]
                }
            })

            return result
        },
    },
}
</script>
